import { useState } from "react";
import { buildBuyerPaymentSummary } from "../domain/buyerPaymentSummary";
import { formatMoney } from "../domain/money";
import type { BuyerFundingFlowPaymentInitiation, BuyerFundingFlowSession } from "../flows/buyerFunding/types";

type Props = {
  session: BuyerFundingFlowSession;
  onInitiated: (initiation: BuyerFundingFlowPaymentInitiation) => void;
};

function sourceAccountSummary(session: BuyerFundingFlowSession): string {
  const plaid = session.plaid;
  if (!plaid || plaid.mode === "skipped") {
    return "Not recorded. The office may request source evidence separately.";
  }
  if (plaid.mode === "manual") {
    return `${plaid.institutionName || "Bank"}, account ending ${plaid.accountLast4 || "not available"} (manual entry)`;
  }
  return `${plaid.institutionName || "Connected bank"}, ending ${plaid.accountLast4 || "not available"} (Plaid)`;
}

/** Amount/fee summary then buyer confirms they sent from their bank (no in-app Transfer). */
export function BuyerPaymentReviewStep({ session, onInitiated }: Props) {
  const [confirmed, setConfirmed] = useState(false);
  const [reference, setReference] = useState("");
  const summary = buildBuyerPaymentSummary({
    purpose: session.paymentRequest.purpose,
    amountDue: session.amountDue
  });

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    onInitiated({
      initiatedAt: new Date().toISOString(),
      rail: session.instructions.paymentRail,
      amountCents: session.amountDue.amountCents,
      reference: reference.trim() || undefined,
      limitation:
        "Buyer-reported initiation only. Veto does not confirm the wire or ACH posted to the trust account. The office decides."
    });
  }

  return (
    <section className="panel" data-testid="buyer-payment-review">
      <dl className="divide-y divide-[#e3e1da] overflow-hidden rounded-xl border border-[#d9d8d0] bg-white text-sm">
        {summary.lines.map((line) => (
          <div key={line.id} className="flex items-center justify-between gap-4 px-3 py-2">
            <dt className={line.emphasis ? "font-bold" : "text-[#596158]"}>{line.label}</dt>
            <dd className={line.emphasis ? "font-bold" : "text-[#596158]"}>
              {line.amount.amountCents === 0 && line.id !== "payment" ? "$0.00" : formatMoney(line.amount)}
            </dd>
          </div>
        ))}
        <div className="flex items-center justify-between gap-4 bg-[#e6f0eb] px-3 py-3">
          <dt className="font-bold">{summary.totalLabel}</dt>
          <dd className="text-lg font-bold">{formatMoney(summary.total)}</dd>
        </div>
      </dl>

      <p className="notice mt-3 text-sm">{summary.feeDisclaimer}</p>

      <dl className="mt-4 grid gap-2 text-sm">
        <div>
          <dt className="font-bold text-[#596158]">Source account (evidence)</dt>
          <dd>{sourceAccountSummary(session)}</dd>
        </div>
      </dl>

      <form className="mt-5 grid gap-3 border-t border-[#e3e1da] pt-4" onSubmit={handleSubmit}>
        <label className="grid gap-1">
          <span className="field-label">Bank reference / confirmation (optional)</span>
          <input
            data-testid="payment-initiation-reference"
            placeholder="Wire confirmation or trace"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
          />
        </label>
        <label className="flex items-start gap-3 rounded-xl border border-[#d9d8d0] bg-[#f7f6f1] p-3">
          <input
            data-testid="payment-initiation-confirm"
            type="checkbox"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
          />
          <span className="text-sm">
            I sent {formatMoney(session.amountDue)} from my bank using the published instructions for file{" "}
            {session.fileCode}.
          </span>
        </label>
        <button
          className="button-primary"
          data-testid="payment-initiation-submit"
          disabled={!confirmed}
          type="submit"
        >
          I sent payment from my bank
        </button>
      </form>
    </section>
  );
}
